import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 * @property {HTMLElement} [cartBubbleAccessibleLabel] - Screen reader cart count label.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  #storedCartCount = 0;

  /** @type {number} */
  get currentCartCount() {
    const inlineCount = this.querySelector('[data-cart-count]');
    const parsedInline = parseInt(inlineCount?.textContent ?? '', 10);
    if (!Number.isNaN(parsedInline)) return parsedInline;

    if (this.#storedCartCount > 0) return this.#storedCartCount;

    const bubbleText = this.refs.cartBubbleCount?.textContent?.trim() ?? '';
    if (bubbleText.endsWith('+')) return this.#storedCartCount || 100;

    const parsedBubble = parseInt(bubbleText, 10);
    return Number.isNaN(parsedBubble) ? 0 : parsedBubble;
  }

  set currentCartCount(value) {
    const count = CartIcon.#normalizeCartCount(value);
    this.#storedCartCount = count;

    if (this.refs.cartBubbleCount) {
      this.refs.cartBubbleCount.textContent = CartIcon.#formatBubbleCount(count);
    }

    const inlineCount = this.querySelector('[data-cart-count]');
    if (inlineCount) {
      inlineCount.textContent = String(count);
    }

    if (this.refs.cartBubble) {
      this.refs.cartBubble.toggleAttribute('data-maintain-ratio', count > 0 && count <= 99);
    }
  }

  /**
   * @param {number} value
   * @returns {number}
   */
  static #normalizeCartCount(value) {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : 0;
  }

  /**
   * Bubble counts cap at 99+; inline desktop CART (n) always shows the full number.
   * @param {number} count
   * @returns {string}
   */
  static #formatBubbleCount(count) {
    return count >= 100 ? '99+' : String(count);
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.syncInitialCartCount();
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        const itemCount = cart?.totalQuantity ?? detail?.itemCount ?? 0;

        this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    const count = CartIcon.#normalizeCartCount(itemCount);

    if (this.refs.cartBubbleCount) {
      this.refs.cartBubbleCount.classList.toggle('hidden', count === 0);
    }

    if (this.refs.cartBubble) {
      this.refs.cartBubble.classList.toggle('visually-hidden', count === 0);
    }

    this.currentCartCount = count;
    this.#updateAccessibleCartLabel(count);

    this.classList.toggle('header-actions__cart-icon--has-cart', count > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(count),
        timestamp: Date.now(),
      })
    );

    if (!animate || count === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Updates the screen reader label referenced by aria-describedby on the cart trigger.
   * @param {number} itemCount
   */
  #updateAccessibleCartLabel(itemCount) {
    const label = this.refs.cartBubbleAccessibleLabel;
    if (!label) return;

    const prefix = Theme.translations?.cart_count ?? 'Total items in cart';
    label.textContent = `${prefix}: ${itemCount}`;
  }

  /**
   * Syncs bubble visibility and inline count from server-rendered markup on first paint.
   */
  syncInitialCartCount = () => {
    const inlineCount = this.querySelector('[data-cart-count]');
    const bubbleCount = this.refs.cartBubbleCount?.textContent?.trim();
    const parsedInline = parseInt(inlineCount?.textContent ?? '', 10);
    const parsedBubble = parseInt(bubbleCount ?? '', 10);
    const initialCount = Number.isNaN(parsedInline) ? (Number.isNaN(parsedBubble) ? 0 : parsedBubble) : parsedInline;

    this.renderCartBubble(initialCount, false);
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');

    if (sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);
      const storedCount = parseInt(value, 10);
      if (Number.isNaN(storedCount)) return;

      const inlineCount = this.querySelector('[data-cart-count]');
      const visibleInline = parseInt(inlineCount?.textContent ?? '', 10);
      const visibleCount = Number.isNaN(visibleInline) ? this.currentCartCount : visibleInline;

      if (storedCount === visibleCount) return;

      if (Date.now() - timestamp < 10000 && storedCount >= 0) {
        this.renderCartBubble(storedCount, false);
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
